import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSnProductMappingInspectionKinds } from '#/api/aftersalesMgmt/snProductMapping/fetchInspectionKinds';
import { replaceSnProductMappingInspectionKinds } from '#/api/aftersalesMgmt/snProductMapping/replaceInspectionKinds';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import { StatusBadge } from '#/modules/commonComponents/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '#/modules/commonComponents/dialog/AlertDialogPrimitives';
import { formatEntityStatusLabel } from '#/modules/commonComponents/table/entityStatus';
import InspectionKindPickerDialog from '#/modules/crmMgmt/aftersales/snProductMapping/components/InspectionKindPickerDialog';
import {
  getSnProductMappingApiErrorMessage,
  shouldReloadSnProductMappingInspectionKinds,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/apiError';
import {
  buildInspectionKindsPayload,
  mapInspectionKindRow,
  mergeInspectionKindSelection,
  sortInspectionKinds,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/inspectionKinds';
import {
  snProductMappingDetailQueryKey,
  snProductMappingInspectionKindsQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function readUpdatedAt(data) {
  return data?.record?.updatedAt ?? null;
}

function readServerRows(data) {
  return sortInspectionKinds((data?.inspectionKinds ?? []).map(mapInspectionKindRow));
}

const SnProductMappingInspectionKindsCard = ({ mappingId, onDetailRefetch }) => {
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [removedInactiveIds, setRemovedInactiveIds] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const seenMappingIdRef = useRef(mappingId);

  const relationQuery = useQuery({
    queryKey: snProductMappingInspectionKindsQueryKey(mappingId),
    queryFn: () => fetchSnProductMappingInspectionKinds(mappingId),
    enabled: Boolean(mappingId),
  });

  const rows = useMemo(
    () => readServerRows(relationQuery.data?.data),
    [relationQuery.data],
  );

  useEffect(() => {
    if (seenMappingIdRef.current === mappingId) {
      return;
    }
    seenMappingIdRef.current = mappingId;
    setRemovedInactiveIds(new Set());
    setSelectedIds(new Set());
  }, [mappingId]);

  const saveMutation = useMutation({
    mutationFn: (nextRows) => replaceSnProductMappingInspectionKinds(
      mappingId,
      buildInspectionKindsPayload(nextRows, readUpdatedAt(relationQuery.data?.data)),
    ),
    retry: false,
    onSuccess: async (result) => {
      toast.success(result?.message || '檢測種類已更新');
      queryClient.setQueryData(snProductMappingInspectionKindsQueryKey(mappingId), result);
      setSelectedIds(new Set());
      setPickerOpen(false);
      setDeleteOpen(false);
      await queryClient.invalidateQueries({
        queryKey: snProductMappingDetailQueryKey(mappingId),
      });
      await onDetailRefetch?.();
    },
    onError: (error) => {
      toast.error(getSnProductMappingApiErrorMessage(error, '儲存檢測種類失敗'));
      if (!shouldReloadSnProductMappingInspectionKinds(error)) {
        return;
      }
      setSelectedIds(new Set());
      setPickerOpen(false);
      setDeleteOpen(false);
      relationQuery.refetch();
      onDetailRefetch?.();
    },
  });

  function rememberRemovedInactive(previousRows, nextRows) {
    const nextIds = new Set(nextRows.map((row) => row.id));
    setRemovedInactiveIds((prev) => {
      const next = new Set(prev);
      previousRows.forEach((row) => {
        if (row.status === 'INACTIVE' && row.id && !nextIds.has(row.id)) {
          next.add(row.id);
        }
      });
      return next;
    });
  }

  function handleConfirm(nextSelectedIds, candidates) {
    const nextRows = mergeInspectionKindSelection(
      rows,
      nextSelectedIds,
      candidates,
      removedInactiveIds,
    );
    rememberRemovedInactive(rows, nextRows);
    saveMutation.mutate(nextRows);
  }

  function handleConfirmDelete() {
    if (saveMutation.isPending) {
      return;
    }
    const remaining = rows.filter((row) => !selectedIds.has(row.id));
    rememberRemovedInactive(rows, remaining);
    saveMutation.mutate(remaining);
  }

  function handleToggleAll(checked) {
    if (checked === true) {
      setSelectedIds(new Set(rows.map((row) => row.id)));
      return;
    }
    setSelectedIds(new Set());
  }

  function handleToggleRow(id, checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked === true) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  const isSaving = saveMutation.isPending;
  const selectedCount = rows.filter((row) => selectedIds.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  let headerChecked = false;
  if (allSelected) {
    headerChecked = true;
  } else if (selectedCount > 0) {
    headerChecked = 'indeterminate';
  }

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="form-block__title">檢測種類</h2>
            <p className="mt-1 text-sm text-gray-600">
              確定選用會立即寫入。要移除時先勾選，再按移除確認。
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={() => {
                setPickerKey((prev) => prev + 1);
                setPickerOpen(true);
              }}
            >
              選擇檢測種類
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={selectedCount === 0 || isSaving || relationQuery.isFetching}
              onClick={() => setDeleteOpen(true)}
            >
              移除
            </Button>
          </div>
        </div>

        {relationQuery.isLoading ? (
          <p className="mt-4 text-sm text-gray-600">載入中…</p>
        ) : null}
        {relationQuery.isError ? (
          <p className="mt-4 text-sm text-destructive">
            {getSnProductMappingApiErrorMessage(relationQuery.error, '載入檢測種類失敗')}
          </p>
        ) : null}
        {!relationQuery.isLoading && !relationQuery.isError && rows.length === 0 ? (
          <p className="mt-4 text-sm text-gray-600">尚未選用檢測種類</p>
        ) : null}

        {rows.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[24rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      <Checkbox
                        checked={headerChecked}
                        disabled={isSaving || rows.length === 0}
                        onCheckedChange={handleToggleAll}
                        aria-label="選取全部檢測種類"
                      />
                      選取
                    </span>
                  </th>
                  <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">順序</th>
                  <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">狀態</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">代號</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">名稱</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-200">
                    <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                      <Checkbox
                        checked={selectedIds.has(row.id)}
                        disabled={isSaving}
                        onCheckedChange={(checked) => handleToggleRow(row.id, checked)}
                        aria-label={`選取 ${row.code || '檢測種類'}`}
                      />
                    </td>
                    <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                      {row.sequence ?? '—'}
                    </td>
                    <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                      <StatusBadge
                        status={row.status}
                        label={formatEntityStatusLabel(row.status)}
                        size="sm"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-gray-900">{row.code || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-900">{row.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {pickerOpen ? (
        <InspectionKindPickerDialog
          key={pickerKey}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          initialSelectedIds={rows.map((row) => row.id)}
          blockedIds={removedInactiveIds}
          onConfirm={handleConfirm}
          isSaving={isSaving}
        />
      ) : null}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent closeOnOutsideClick={false} closeOnEscape={!isSaving}>
          <AlertDialogHeader>
            <AlertDialogTitle>移除檢測種類</AlertDialogTitle>
            <AlertDialogDescription>
              {`將移除已勾選的 ${selectedCount} 項，並立即寫入。`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>取消</AlertDialogCancel>
            <AlertDialogAction disabled={isSaving} onClick={handleConfirmDelete}>
              確認移除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

SnProductMappingInspectionKindsCard.propTypes = {
  mappingId: PropTypes.string.isRequired,
  onDetailRefetch: PropTypes.func,
};

export default SnProductMappingInspectionKindsCard;
